import { AiOutlineLoading } from "react-icons/ai";
import { useSearchParams } from "react-router-dom";
import { useNewAgent } from "../contexts/NewAgentContext";
import {
  AGENT_TYPES,
  AUTONOMOUS_AGENT_STATUS,
  CREATE_AGENT_CATEGORIES,
} from "constants/agents";
import { useTranslation } from "hooks/useTranslation";

export default function StepperControl(props: {
  steps: {
    stepNo: number;
    name: string;
    highlighted?: boolean;
    selected?: boolean;
    completed?: boolean;
  }[];
  currentStep: number;
  handleClick: any;
}) {
  const { steps, currentStep, handleClick } = props;
  const { formData, handleSubmit, isLoading } = useNewAgent();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const agentCategory = searchParams.get(
    "category"
  ) as CREATE_AGENT_CATEGORIES | null;

  const isOrchestrator = formData.agent_type === AGENT_TYPES.ORCHESTRATOR;
  // For FUNCTIONAL, check URL parameter or if agent_type is a functional type (e.g., "autonomous")
  const isFunctional =
    agentCategory === CREATE_AGENT_CATEGORIES.FUNCTIONAL ||
    formData.agent_type === AGENT_TYPES.AUTONOMOUS;

  const isDisabled = () => {
    if (isOrchestrator) {
      // Orchestrator flow: Agent Details -> Choose Workflow -> Assign Specialists -> Finish
      switch (currentStep) {
        case 1:
          return !formData.name;
        case 2:
          // Step 2: Choose Workflow - optional for orchestrator
          return false;
        case 3:
          // Step 3: Assign Specialists - required for orchestrator
          return !formData.agent_ids || formData.agent_ids.length === 0;
        default:
          return false;
      }
    } else if (isFunctional) {
      // Functional flow: Agent Details -> Trigger -> Pipeline -> Finish
      switch (currentStep) {
        case 1:
          // Step 1: Agent Details - name and agent_type are required
          return !formData.name || !formData.agent_type;
        case 2:
          // Step 2: Trigger - optional for functional
          // If schedule is selected, cron is required, otherwise it's optional
          if (formData.trigger_type === AUTONOMOUS_AGENT_STATUS.SCHEDULING) {
            return !formData.cron;
          }
          return false; // Trigger step is optional
        case 3:
          // Step 3: Pipeline - required for functional
          // At least one pipeline item with all fields filled is required
          return (
            !formData.pipelines ||
            formData.pipelines.length === 0 ||
            formData.pipelines.some((p) => !p.input || !p.agent_id || !p.output)
          );
        default:
          return false;
      }
    } else {
      // Specialist flow: Agent Details -> Knowledge Base -> Choose Workflow -> Integration -> Finish
      switch (currentStep) {
        case 1:
          return !formData.name;
        case 2:
          // Step 2: Knowledge Base - required for specialist
          return formData.knowledgeBase.length === 0;
        case 3:
          // Step 3: Choose Workflow - optional for specialist
          return false;
        case 4:
          // Step 4: Integration - optional
          return false;
        default:
          return false;
      }
    }
  };

  const handleNextOrSubmit = async () => {
    if (currentStep === steps.length - 1) {
      await handleSubmit();
      handleClick("next");
    } else {
      handleClick("next");
    }
  };

  return (
    <>
      {currentStep !== steps.length && (
        <div className="mt-2 flex flex-col justify-between   px-3 pt-2 md:flex-row md:px-8">
          {currentStep === 1 ? (
            <div />
          ) : (
            <button
              onClick={() => handleClick()}
              disabled={isLoading}
              className={`mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0`}
            >
              {t("agents.newAgent.prev")}
            </button>
          )}

          <button
            onClick={handleNextOrSubmit}
            disabled={isDisabled() || isLoading}
            className="cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <AiOutlineLoading className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></AiOutlineLoading>
                {t("agents.newAgent.loading")}
              </div>
            ) : currentStep === steps.length - 1 ? (
              t("agents.newAgent.submit")
            ) : (
              t("agents.newAgent.next")
            )}
          </button>
        </div>
      )}
    </>
  );
}
